import { ReactNode, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Stack, IconButton, Typography, Button } from '@mui/material'
import { Add, Remove } from '@mui/icons-material'
import LoadingButton from '@mui/lab/LoadingButton/LoadingButton'
import { formatTemperatureDelta } from '../utils/string'
import AuroraControl from './AuroraControl'

export interface AuroraHeatingElementOffsetTemperatureControlProps {
  label: ReactNode
  value: number | null
  targetSupplyDelta?: number | null
  loading?: boolean
  disabled?: boolean
  onSubmit: (value: number) => Promise<void>
}

export default function AuroraHeatingElementOffsetTemperatureControl({
  label,
  value,
  targetSupplyDelta = null,
  loading = false,
  disabled = false,
  onSubmit
}: AuroraHeatingElementOffsetTemperatureControlProps) {
  const { t } = useTranslation('aurora')

  const [offsetTemperature, setOffsetTemperature] = useState<number | null>(null)

  function handleIncrement() {
    setOffsetTemperature((prev) => {
      const current = prev !== null ? prev : (value ?? 0)
      return Math.ceil(current * 2) / 2 + 0.5
    })
  }

  function handleDecrement() {
    setOffsetTemperature((prev) => {
      const current = prev !== null ? prev : (value ?? 0)
      return Math.ceil(current * 2) / 2 - 0.5
    })
  }

  async function handleSubmit() {
    if (offsetTemperature === null) {
      return
    }

    try {
      await onSubmit(offsetTemperature)

      setOffsetTemperature(null)
    } catch {
      // Ignore the error.
    }
  }

  function handleReset() {
    setOffsetTemperature(null)
  }

  return (
    <AuroraControl
      direction="column"
      label={label}
      loading={loading}
      disabled={disabled}
      control={
        <Stack direction="column" justifyContent="center" alignItems="center" gap={2} pt={2}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" gap={3}>
            <IconButton
              color={offsetTemperature !== null ? 'primary' : 'inherit'}
              disabled={loading || disabled}
              size="small"
              sx={{ border: '1px solid' }}
              onClick={handleDecrement}
            >
              <Remove />
            </IconButton>

            {offsetTemperature !== null ? (
              <Typography variant="h2" color={loading ? 'text.disabled' : 'primary'}>
                {formatTemperatureDelta(offsetTemperature)}
              </Typography>
            ) : (
              <Typography variant="h2" color="text.primary">
                {formatTemperatureDelta(value ?? 0)}
              </Typography>
            )}

            <IconButton
              color={offsetTemperature !== null ? 'primary' : 'inherit'}
              disabled={loading || disabled}
              size="small"
              sx={{
                border: '1px solid'
              }}
              onClick={handleIncrement}
            >
              <Add />
            </IconButton>
          </Stack>

          {targetSupplyDelta !== null && (
            <Typography color="text.secondary">
              {`ΔT: ${targetSupplyDelta !== null ? formatTemperatureDelta(targetSupplyDelta) : '-'}`}
            </Typography>
          )}

          {offsetTemperature !== null && (
            <Stack direction="row" alignItems="center" gap={2}>
              <LoadingButton
                variant="outlined"
                color="primary"
                size="small"
                loading={loading}
                disabled={disabled}
                onClick={handleSubmit}
              >
                {t('actions:confirm')}
              </LoadingButton>

              {!loading && (
                <Button
                  variant="text"
                  color="inherit"
                  size="small"
                  disabled={disabled}
                  onClick={handleReset}
                >
                  {t('actions:cancel')}
                </Button>
              )}
            </Stack>
          )}
        </Stack>
      }
    />
  )
}
